import type { Rate, RatedProtectionProduct } from '@makemydeal/dr-platform-types';
import type { DraftDealProduct } from '@makemydeal/dr-dash-types';
import { formatNumber, formatDollarsAndCents, isValidNumber } from '@makemydeal/dr-common-utils';
import { UNLIMITED_MILEAGE_LABEL } from '../constants';

export const joinDeductibleValueAndMethod = (deductibleValue?: number, deductibleMethod?: string) => {
    if (!isValidNumber(deductibleValue as number)) {
        return deductibleMethod || '';
    }

    const formattedNumericValue = formatDollarsAndCents(deductibleValue);

    return deductibleMethod ? `${formattedNumericValue} ${deductibleMethod}` : formattedNumericValue;
};

export const parseDeductible = (
    deductibleString: string
): { deductibleValue: number | undefined; deductibleApplyType: string | undefined } => {
    let deductibleValue, deductibleApplyType;

    try {
        ({ deductibleValue, deductibleApplyType } = JSON.parse(deductibleString));
    } catch (error) {
        /* empty */
    }

    return { deductibleValue, deductibleApplyType };
};

export const getDeductibleValue = (deductibleValue: number | undefined, deductibleApplyType: string | undefined): string => {
    return JSON.stringify({ deductibleValue, deductibleApplyType });
};

/**
 * Detects whether a single token is a number, allowing an optional leading `$`, thousands separators and decimals
 * (e.g. "200", "200.5", "$200.00", "1,000"). Returns the parsed number, or undefined when the token is not numeric.
 */
const parseNumericToken = (token: string): number | undefined => {
    if (!/^\$?\d{1,3}(,\d{3})+(\.\d+)?$|^\$?\d+(\.\d+)?$/.test(token)) {
        return undefined;
    }
    return Number(token.replace(/[$,]/g, ''));
};

/**
 * Parses a free-text deductible override into its numeric value and apply-method:
 *  - a number at the start (e.g. "$200.00 Disappearing") -> value is the leading number, the rest is the method.
 *  - a number at the end (e.g. "Per Occurrence 100") -> value is the trailing number, the preceding text is the method.
 *  - a lone number (e.g. "250") -> value only, no method.
 *  - a lone text / text without a boundary number (e.g. "Reducing") -> method only, no value.
 * This never drops what the user typed: text that is not a boundary number is preserved entirely as the method.
 */
export const parseManualDeductible = (
    input: string
): { productDeductible: number | undefined; productDeductibleMethod: string | undefined } => {
    const trimmed = input.trim();

    if (!trimmed) {
        return { productDeductible: undefined, productDeductibleMethod: undefined };
    }

    const tokens = trimmed.split(/\s+/);
    const firstValue = parseNumericToken(tokens[0]);
    const lastValue = tokens.length > 1 ? parseNumericToken(tokens[tokens.length - 1]) : undefined;

    if (firstValue !== undefined) {
        return { productDeductible: firstValue, productDeductibleMethod: tokens.slice(1).join(' ') || undefined };
    }

    if (lastValue !== undefined) {
        return { productDeductible: lastValue, productDeductibleMethod: tokens.slice(0, -1).join(' ') };
    }

    return { productDeductible: undefined, productDeductibleMethod: trimmed };
};

/**
 * Builds the raw text-input value for the manual deductible override (e.g. "500 Reducing").
 * Unlike `joinDeductibleValueAndMethod`, the numeric portion is left unformatted so the user can edit it freely.
 */
export const getManualDeductibleInputValue = (deductibleValue?: number, deductibleMethod?: string): string => {
    const valuePart = isValidNumber(deductibleValue as number) ? String(deductibleValue) : '';
    const methodPart = deductibleMethod ?? '';

    return [valuePart, methodPart].filter((part) => part !== '').join(' ');
};

export const hasCorrectPlan = (
    rates: Rate[],
    rateAttributes: RatedProtectionProduct['rateAttributes'],
    productProviderPlan: string | undefined
) => !rateAttributes.includes('productProviderPlan') || rates.some((rate) => rate.productProviderPlan === productProviderPlan);

export const hasCorrectTerm = (
    rates: Rate[],
    rateAttributes: RatedProtectionProduct['rateAttributes'],
    productCoverageLength: number | undefined
) =>
    !rateAttributes.includes('productCoverageLength') || rates.some((rate) => rate.productCoverageLength === productCoverageLength);

export const hasCorrectMiles = (
    rates: Rate[],
    rateAttributes: RatedProtectionProduct['rateAttributes'],
    productCoverageMiles: number | undefined
) => !rateAttributes.includes('productCoverageMiles') || rates.some((rate) => rate.productCoverageMiles === productCoverageMiles);

export const hasCorrectDeductible = (
    rates: Rate[],
    rateAttributes: RatedProtectionProduct['rateAttributes'],
    productDeductible: number | undefined,
    productDeductibleMethod: string | undefined
) =>
    !rateAttributes.includes('productDeductible') ||
    rates.some(
        (rate) =>
            joinDeductibleValueAndMethod(rate.productDeductible, rate.deductibleApplyType) ===
            joinDeductibleValueAndMethod(productDeductible, productDeductibleMethod)
    );

export const hasCorrectServiceInterval = (
    rates: Rate[],
    rateAttributes: RatedProtectionProduct['rateAttributes'],
    productServiceInterval: string | undefined
) =>
    !rateAttributes.includes('productServiceInterval') ||
    rates.some((rate) => rate.productServiceInterval === productServiceInterval);

export const isUnlimitedMileage = (mileage?: number) => !mileage || mileage <= 0 || mileage >= 999_999;

/**
 * Silly little utility to selectively check or skip checking a plan
 * while the feature toggle for plan selection could be on or off.
 */
const comparePlan = (planA: string | undefined, planB: string | undefined) => {
    return planA === planB;
};

export const getTermsOptions = (rates: Rate[], productPlan: string | undefined) => {
    const ratesOfSamePlan = rates.filter((rate) => rate.productProviderPlan === productPlan);
    return Array.from(new Set(ratesOfSamePlan.map((rate) => rate.productCoverageLength)), (term) => ({
        value: formatNumber(term as number),
        label: formatNumber(term as number)
    }));
};

export const getMilesOptions = (rates: Rate[], product: DraftDealProduct) => {
    return Array.from(
        rates.reduce((acc, rate) => {
            if (
                rate.productCoverageMiles !== undefined &&
                comparePlan(rate.productProviderPlan, product.productProviderPlan) &&
                rate.productCoverageLength === product.productCoverageLength
            ) {
                acc.add(rate.productCoverageMiles);
            }
            return acc;
        }, new Set<number>())
    ).map((mile) => ({
        value: formatNumber(mile),
        label: isUnlimitedMileage(mile) ? UNLIMITED_MILEAGE_LABEL : formatNumber(mile)
    }));
};

export const getDeductibleOptions = (rates: Rate[], product: DraftDealProduct) => {
    type DeductibleInfo = { productDeductible?: number; deductibleApplyType?: string };

    const deductibleSet = new Set<string>();

    const deductibleOptions: DeductibleInfo[] = rates.reduce((acc, rate) => {
        const isDeductibleDefined = rate.productDeductible !== undefined || rate.deductibleApplyType !== undefined;

        const isProductCoverageLengthDefinedAndSame =
            rate.productCoverageLength !== undefined && rate.productCoverageLength === product.productCoverageLength;

        const isProductCoverageMilesDefinedAndSame =
            rate.productCoverageMiles !== undefined && rate.productCoverageMiles === product.productCoverageMiles;

        if (
            isDeductibleDefined &&
            isProductCoverageLengthDefinedAndSame &&
            isProductCoverageMilesDefinedAndSame &&
            comparePlan(rate.productProviderPlan, product.productProviderPlan)
        ) {
            const { productDeductible, deductibleApplyType } = rate;
            const key = `${productDeductible}_${deductibleApplyType}`;

            if (!deductibleSet.has(key)) {
                deductibleSet.add(key);
                acc.push({ productDeductible, deductibleApplyType });
            }
        }
        return acc;
    }, [] as DeductibleInfo[]);

    return deductibleOptions.map(({ productDeductible, deductibleApplyType }) => {
        const formattedValue = joinDeductibleValueAndMethod(productDeductible, deductibleApplyType);

        return {
            value: getDeductibleValue(productDeductible, deductibleApplyType),
            label: formattedValue
        };
    });
};

export const getIntervalOptions = (rates: Rate[], product: DraftDealProduct) => {
    return Array.from(
        rates.reduce((acc, rate) => {
            if (
                rate.productServiceInterval !== undefined &&
                comparePlan(rate.productProviderPlan, product.productProviderPlan) &&
                rate.productCoverageLength === product.productCoverageLength &&
                rate.productCoverageMiles === product.productCoverageMiles &&
                rate.productDeductible === product.productDeductible
            ) {
                acc.add(rate.productServiceInterval);
            }
            return acc;
        }, new Set<string>())
    ).map((interval) => ({ value: interval, label: interval }));
};

export const findRate = (rates: Rate[], attributes: Partial<Rate>): Rate | undefined => {
    const attributeKeys = Object.keys(attributes) as (keyof Rate)[];
    return rates.find((rate) => attributeKeys.every((key) => rate[key] === attributes[key]));
};
